import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../components/ui/dialog";

/**
 * Overview of all keyboard shortcuts of the design and video editor („?“ or
 * the menu). Keys are written with tokens (`mod`, `shift`, `alt`) and shown
 * with the symbols of the platform (⌘ ⇧ ⌥ on the Mac, Strg/Umschalt/Alt elsewhere).
 */

export const SHORTCUTS_EVENT = "kaleido-shortcuts";

export function openShortcuts() {
  window.dispatchEvent(new CustomEvent(SHORTCUTS_EVENT));
}

/** One way to press a shortcut: tokens pressed together. */
type Combo = string[];

interface Shortcut {
  label: string;
  /** Alternatives („oder“). */
  keys: Combo[];
}

interface Group {
  title: string;
  items: Shortcut[];
  /** Only for designs / only for videos. */
  only?: "design" | "video";
}

const k = (...combos: string[]): Combo[] => combos.map((combo) => combo.split("+"));

export const SHORTCUT_GROUPS: Group[] = [
  {
    title: "Allgemein",
    items: [
      { label: "Suchen (Bausteine, Medien, Seiten)", keys: k("mod+K") },
      { label: "Rückgängig", keys: k("mod+Z") },
      { label: "Wiederholen", keys: k("shift+mod+Z", "mod+Y") },
      { label: "Speichern", keys: k("mod+S") },
      { label: "Exportieren", keys: k("mod+E") },
      { label: "Tastenkürzel anzeigen", keys: k("?") },
    ],
  },
  {
    title: "Suchen & Ersetzen",
    only: "design",
    items: [
      { label: "Im Dokument suchen", keys: k("mod+F") },
      { label: "Suchen und ersetzen", keys: k("alt+mod+F") },
      { label: "Nächster / vorheriger Treffer", keys: k("enter", "shift+enter") },
      { label: "Alle ersetzen (im Feld „Ersetzen durch“)", keys: k("mod+enter") },
    ],
  },
  {
    title: "Video",
    only: "video",
    items: [
      { label: "Abspielen / Pause", keys: k("space") },
      { label: "Am Abspielkopf teilen", keys: k("S") },
      { label: "Ein Bild vor / zurück", keys: k("right", "left") },
      { label: "1 Sekunde vor / zurück", keys: k("shift+right", "shift+left") },
      { label: "Zum Anfang / Ende", keys: k("home", "end") },
      { label: "Clip löschen", keys: k("backspace") },
      { label: "Clip duplizieren", keys: k("mod+D") },
      { label: "Clip-Auswahl aufheben", keys: k("escape") },
      { label: "Zeitleiste zoomen", keys: k("mod+scroll") },
    ],
  },
  {
    title: "Ansicht",
    items: [
      { label: "Vergrößern / Verkleinern", keys: k("mod+plus", "mod+minus") },
      { label: "Einpassen", keys: k("mod+0") },
      { label: "Auf 100 %", keys: k("mod+1") },
      { label: "Zoomen mit dem Mausrad", keys: k("mod+scroll") },
      { label: "Ansicht verschieben", keys: k("space+drag") },
      { label: "Lineale / Hilfslinien / Raster", keys: k("shift+R", "shift+H", "shift+G") },
      { label: "Ebenen öffnen", keys: k("shift+L") },
      { label: "Nächste / vorherige Seite", keys: k("pagedown", "pageup") },
    ],
  },
  {
    title: "Einfügen",
    items: [
      { label: "Text", keys: k("T") },
      { label: "Rechteck / Ellipse / Linie", keys: k("R", "O", "L") },
      { label: "Bild aus der Zwischenablage", keys: k("mod+V") },
    ],
  },
  {
    title: "Auswahl & Bearbeiten",
    items: [
      { label: "Farbpipette (im unterstützten Browser)", keys: k("I") },
      { label: "Alles auswählen", keys: k("mod+A") },
      { label: "Zur Auswahl hinzufügen", keys: k("shift+click") },
      { label: "Auswahl aufheben / Zuschneiden beenden", keys: k("escape") },
      { label: "Verschieben um 1 px / 10 px", keys: k("arrows", "shift+arrows") },
      { label: "Kopieren / Ausschneiden / Einfügen", keys: k("mod+C", "mod+X", "mod+V") },
      { label: "Duplizieren", keys: k("mod+D") },
      { label: "Löschen", keys: k("backspace") },
      { label: "Text bearbeiten / Bild zuschneiden", keys: k("enter") },
      { label: "Gruppieren / Gruppierung aufheben", keys: k("mod+G", "shift+mod+G") },
      { label: "Sperren / Entsperren", keys: k("mod+L") },
      { label: "Ausblenden / Einblenden", keys: k("shift+mod+H") },
      { label: "Eine Ebene nach vorne / hinten", keys: k("mod+]", "mod+[") },
      { label: "Ganz nach vorne / hinten", keys: k("shift+mod+]", "shift+mod+[") },
      { label: "Format kopieren / einfügen", keys: k("alt+mod+C", "alt+mod+V") },
    ],
  },
  {
    title: "Ziehen & Transformieren",
    items: [
      { label: "Verschieben ohne Einrasten", keys: k("mod+drag") },
      { label: "Seitenverhältnis beibehalten / lösen", keys: k("shift+drag") },
      { label: "Von der Mitte aus skalieren", keys: k("alt+drag") },
      { label: "Drehen in festen Schritten", keys: k("shift+rotate") },
      { label: "Bild auf Bild ablegen, ohne zu ersetzen", keys: k("alt+drop") },
    ],
  },
  {
    title: "Text",
    items: [
      { label: "Fett / Kursiv / Unterstrichen", keys: k("mod+B", "mod+I", "mod+U") },
      { label: "Durchgestrichen", keys: k("shift+mod+X") },
      { label: "Aufzählung / Nummerierte Liste", keys: k("shift+mod+8", "shift+mod+7") },
      { label: "Einrücken / Ausrücken", keys: k("tab", "shift+tab") },
      { label: "Bearbeiten beenden", keys: k("escape", "mod+enter") },
    ],
  },
  {
    title: "Präsentieren",
    only: "design",
    items: [
      { label: "Nächste Seite", keys: k("right", "space", "click") },
      { label: "Vorherige Seite", keys: k("left") },
      { label: "Erste / letzte Seite", keys: k("home", "end") },
      { label: "Zu Seite springen", keys: k("number+enter") },
      { label: "Schwarzbild", keys: k("B") },
      { label: "Laserpointer", keys: k("L") },
      { label: "Beenden", keys: k("escape") },
    ],
  },
];

export function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
}

const MAC: Record<string, string> = {
  mod: "⌘",
  shift: "⇧",
  alt: "⌥",
  enter: "↵",
  backspace: "⌫",
  escape: "Esc",
  tab: "⇥",
};

const OTHER: Record<string, string> = {
  mod: "Strg",
  shift: "Umschalt",
  alt: "Alt",
  enter: "Enter",
  backspace: "Entf",
  escape: "Esc",
  tab: "Tab",
};

const COMMON: Record<string, string> = {
  space: "Leertaste",
  left: "←",
  right: "→",
  arrows: "Pfeiltasten",
  home: "Pos1",
  end: "Ende",
  pageup: "Bild ↑",
  pagedown: "Bild ↓",
  plus: "+",
  minus: "−",
  scroll: "Scrollen",
  drag: "Ziehen",
  click: "Klick",
  rotate: "Drehen",
  drop: "Ablegen",
  number: "Zahl",
};

/** Label of one key token on this platform. */
export function keyLabel(token: string, mac = isMac()): string {
  return (mac ? MAC : OTHER)[token] ?? COMMON[token] ?? token;
}

function ComboView({ combo, mac }: { combo: Combo; mac: boolean }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {combo.map((token, index) => (
        <kbd
          // biome-ignore lint/suspicious/noArrayIndexKey: tokens of a fixed combo
          key={index}
          className="min-w-6 rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-center font-sans text-[11px] text-zinc-700 shadow-[0_1px_0_rgba(0,0,0,0.06)]"
        >
          {keyLabel(token, mac)}
        </kbd>
      ))}
    </span>
  );
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** The dialog; opens with „?“ (Shift+ß on German keyboards) or `openShortcuts()`. */
export function ShortcutsDialog({ video = false }: { video?: boolean }) {
  const [open, setOpen] = useState(false);
  const mac = isMac();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "?" || event.metaKey || event.ctrlKey) return;
      if (isTyping(event.target) || document.querySelector("[role=dialog]")) return;
      event.preventDefault();
      setOpen(true);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(SHORTCUTS_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(SHORTCUTS_EVENT, onOpen);
    };
  }, []);
  const groups = SHORTCUT_GROUPS.filter(
    (group) => !group.only || group.only === (video ? "video" : "design"),
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        title="Tastenkürzel"
        description={
          video
            ? "Alle Kürzel des Video-Editors. Drücke jederzeit „?“, um diese Übersicht zu öffnen."
            : "Alle Kürzel des Design-Editors. Drücke jederzeit „?“, um diese Übersicht zu öffnen."
        }
        className="max-w-4xl"
      >
        <div className="max-h-[65vh] columns-1 gap-8 overflow-y-auto pr-1 md:columns-2">
          {groups.map((group) => (
            <section key={group.title} className="mb-5 break-inside-avoid">
              <h3 className="mb-1.5 text-xs font-semibold text-zinc-700">{group.title}</h3>
              <dl className="space-y-1">
                {group.items.map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-3 py-0.5">
                    <dt className="text-sm text-zinc-700">{item.label}</dt>
                    <dd className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                      {item.keys.map((combo, index) => (
                        <span key={combo.join("+")} className="inline-flex items-center gap-1">
                          {index > 0 ? <span className="text-[11px] text-zinc-400">/</span> : null}
                          <ComboView combo={combo} mac={mac} />
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
